// ============================================================================
// Mon Temps d'Étude — Banque fin CE1 (entrée CE2)
// Production conforme spec v2.1 ratifiée par Ana le 19/09/2026
// Worktree: feature/lumen-diag-bank-2026-09
// Auteur: Lumen MTE <lumen@mte.local>
// Date production: 2026-09-19
// ============================================================================
//
// PATTERN UNIVERSEL: target = CE2 → diagnostique les acquis de fin de CE1.
// Banque resegmentation de l'ancien `banque-questions-canon.ts` (43 items CE1/CM1).
//
// SOURCES OFFICIELLES (cf spec §5):
//   - CE1 fluence: ≥ 70 mots/min (BO n°41 du 31 octobre 2024 Annexe 3)
//   - CE1 numération: jusqu'à 1 000 (BO n°41 du 31 octobre 2024 Annexe 4)
// ============================================================================

import type { BanqueItem } from '../lib/banque-types';

type CE1Item = BanqueItem;  // schoolLevel: 'CE1' natif

const ITEMS_LECTURE_CODE: CE1Item[] = [
  {
    id: 'FR-CE1-LEC-01',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'FR-CE1-LEC-01',
    enonce: "Combien de syllabes a le mot 'carrefour' ? (réponse en chiffre)",
    bonneReponse: '3',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE1 · Lecture · mots longs',
      preuve_url: null,
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-LEC-02',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-LEC-02',
    enonce: "Quel mot contient le son [f] ?",
    options: ['chemise', 'photo', 'mouche', 'chemin'],
    bonneReponse: 'photo',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Lecture · discrimination auditive',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-LEC-03',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'FR-CE1-LEC-03',
    enonce: "Combien de syllabes a le mot 'camionnette' ? (réponse en chiffre)",
    bonneReponse: '4',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Lecture · découpage syllabique',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-LEC-04',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-LEC-04',
    enonce: "Quel mot se prononce avec [ʃ] (ch) ?",
    options: ['cheval', 'genre', 'jambe', 'guêpe'],
    bonneReponse: 'cheval',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Lecture · sons complexes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-LEC-05',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-LEC-05',
    enonce: "Quel mot est correctement écrit ?",
    options: ['un gand', 'un grand', 'un gran', 'un gant'],
    bonneReponse: 'un grand',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Lecture · orthographe lexicale',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-LEC-06',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-LEC-06',
    enonce: "Comment se prononce 'gn' dans 'montagne' ?",
    options: ['[g]+[n]', '[ɲ]', '[ŋ]', 'muet'],
    bonneReponse: '[ɲ]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Lecture · graphèmes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_FLUENCE: CE1Item[] = [
  {
    id: 'FR-CE1-FLU-01',
    schoolLevel: 'CE1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE1-FLU-01',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Aujourd'hui, la classe va au zoo. Les enfants prennent le car. Ils découvrent les girafes, les éléphants et les lions. Le guide explique les habitudes de chaque animal. Les enfants posent beaucoup de questions.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: 'La sortie au zoo',
      mots_total: 41,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · fluence · entrainement',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 70,
    },
  },
  {
    id: 'FR-CE1-FLU-02',
    schoolLevel: 'CE1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE1-FLU-02',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Au pied de la montagne, il y avait un petit ruisseau qui chantait. Les oiseaux venaient boire. Les enfants du village venaient jouer près de lui. Le vieux berger s'y reposait. Tous aimaient ce ruisseau mystérieux. Il coulait jour et nuit, sans jamais se plaindre.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: 'Le ruisseau qui chantait',
      mots_total: 66,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · fluence · cible 70 mots/min',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 70,
    },
  },
  {
    id: 'FR-CE1-FLU-03',
    schoolLevel: 'CE1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE1-FLU-03',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Un petit garçon joue dans le jardin. Le vent souffle fort dans les arbres. Il enlève le chapeau du petit garçon et le fait voler. Le petit garçon court après son chapeau en riant. Finalement, le vent pose le chapeau sur la tête du chat qui dort.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: 'Le petit garçon et le vent',
      mots_total: 70,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · fluence · cible 70 mots/min · evaluation formelle',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 70,
    },
  },
];

const ITEMS_COMPREHENSION: CE1Item[] = [
  {
    id: 'FR-CE1-COMP-01',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-COMP-01',
    enonce: "Lis : 'Marc et Léa jouent dans la cour. Marc court très vite. Léa saute à la corde.' Qui court très vite ?",
    options: ['Léa', 'Marc', 'Les deux', 'Aucun'],
    bonneReponse: 'Marc',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · information explicite',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-COMP-02',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-COMP-02',
    enonce: "Lis : 'Sophie a perdu son cartable. Elle est triste car elle ne peut pas faire ses devoirs ce soir.' Pourquoi Sophie est triste ?",
    options: ["Elle n'aime pas l'école", 'Elle ne peut pas faire ses devoirs', 'Ses amis ne viennent pas', 'Il pleut dehors'],
    bonneReponse: 'Elle ne peut pas faire ses devoirs',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · inférence simple',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-COMP-03',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-COMP-03',
    enonce: "Lis : 'Pierre lui a donné un livre pour son anniversaire. Il lui a aussi écrit une belle carte.' De qui parle-t-on avec 'Il' ?",
    options: ['Le livre', "L'anniversaire", 'Pierre', 'La carte'],
    bonneReponse: 'Pierre',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · chaîne anaphorique',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-COMP-04',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-COMP-04',
    enonce: "Lis : 'Le jardinier arrose les fleurs parce qu'il fait très chaud et qu'elles ont soif.' Pourquoi les fleurs sont arrosées ?",
    options: ['Il pleut', 'Il fait froid', 'Il fait chaud', 'C\'est la nuit'],
    bonneReponse: 'Il fait chaud',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · inférence complexe',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-COMP-05',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'FR-CE1-COMP-05',
    enonce: "Lis : 'Maman prépare un gâteau au chocolat. Le four sonne. Le gâteau est prêt. Toute la famille le mange.' Quel parfum a ce gâteau ?",
    consigne: 'Réponds en un seul mot (sans déterminant, en minuscules).',
    bonneReponse: 'chocolat',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · restitution',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-COMP-06',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-COMP-06',
    enonce: "Lis : 'Dans ce village, on raconte qu'un fantôme hante le vieux château. Les enfants n'osent plus y jouer le soir.' Comment appelle-t-on ces histoires ?",
    options: ['Des documentaires', 'Des histoires de fantômes', "Des histoires d'amour", 'Des publicités'],
    bonneReponse: 'Des histoires de fantômes',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Compréhension · identifier genre',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_VOCABULAIRE: CE1Item[] = [
  {
    id: 'FR-CE1-VOC-01',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-VOC-01',
    enonce: "Quel mot veut dire la même chose que 'gentil' ?",
    options: ['méchant', 'sage', 'bruyant', 'rapide'],
    bonneReponse: 'sage',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire · synonymes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-VOC-02',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-VOC-02',
    enonce: "Quel mot veut dire le contraire de 'ouvert' ?",
    options: ['fermé', 'plein', 'vide', 'grand'],
    bonneReponse: 'fermé',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire · antonymes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-VOC-03',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-VOC-03',
    enonce: "Quel préfixe utiliserais-tu pour dire 'à l'envers' ?",
    options: ['re-', 'dé-', 'in-', 'im-'],
    bonneReponse: 'dé-',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire · préfixes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-VOC-04',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-VOC-04',
    enonce: "Quel mot appartient à la famille de 'fleur' ?",
    options: ['fleuve', 'fleurir', 'flèche', 'forêt'],
    bonneReponse: 'fleurir',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire · familles de mots',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-VOC-05',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'FR-CE1-VOC-05',
    enonce: "Trouve un mot de la famille de 'soleil'. Réponse en un mot.",
    bonneReponse: 'ensoleillé',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-VOC-06',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-VOC-06',
    enonce: "Quel mot est formé avec le suffixe '-able' ?",
    options: ['mangeable', 'manger', 'mangeoire', 'mangée'],
    bonneReponse: 'mangeable',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Vocabulaire · suffixes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_ECRITURE_ORTHOGRAPHE: CE1Item[] = [
  {
    id: 'FR-CE1-ORTH-01',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'FR-CE1-ORTH-01',
    enonce: "Écris le pluriel de 'un animal' (plusieurs).",
    bonneReponse: 'des animaux',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Écriture · pluriel -al → -aux",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-ORTH-02',
    schoolLevel: 'CE1',
    archetype: 'dictee',
    competence: 'FR-CE1-ORTH-02',
    enonce: 'Écris la phrase que tu entends.',
    audioUrl: '/assets/dictee/ce1/le-chat-noir-dort.wav',
    bonneReponse: 'Le chat noir dort.',
    replayMax: 3,
    evalMode: 'HUMAN',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Écriture · dictée phrases courtes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
  {
    id: 'FR-CE1-ORTH-03',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-ORTH-03',
    enonce: "Quelle phrase est correcte ?",
    options: ['la fille joue', 'la fille joues', 'le fille joue', 'la fille jouer'],
    bonneReponse: 'la fille joue',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Écriture · accord sujet-verbe initiation',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-ORTH-04',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-ORTH-04',
    enonce: "Quel est le bon accord du participe passé ?",
    options: ["Les fleurs que j'ai cueilli", "Les fleurs que j'ai cueillies", "Les fleurs que j'ai cueillis", 'Les fleurs que j\'ai cueillir'],
    bonneReponse: "Les fleurs que j'ai cueillies",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Écriture · accord participe passé',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CONSIGNES: CE1Item[] = [
  {
    id: 'FR-CE1-CONS-01',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-CONS-01',
    enonce: 'Consigne : "Recopie les mots soulignés et écris leur nombre". Que faire ?',
    options: ['Souligner les mots', 'Recopier les mots soulignés et donner leur nombre', 'Effacer les mots', 'Compter les phrases'],
    bonneReponse: 'Recopier les mots soulignés et donner leur nombre',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Comprendre une consigne 2 temps',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE1-CONS-02',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'FR-CE1-CONS-02',
    enonce: 'Consigne : "Souligne en rouge les verbes et en bleu les noms". Combien de couleurs ?',
    options: ['1', '2', '3', '4'],
    bonneReponse: '2',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Comprendre une consigne 2 temps + 2 couleurs',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_NUMERATION: CE1Item[] = [
  {
    id: 'MA-CE1-NB-01',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'MA-CE1-NB-01',
    enonce: 'Quel nombre est le plus grand ?',
    options: ['456', '546', '465', '564'],
    bonneReponse: '546',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · comparer jusqu'à 1 000",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      champ_officiel: "nombres jusqu'à 1 000",
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-NB-02',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-NB-02',
    enonce: 'Écris le nombre huit cent soixante-douze en chiffres.',
    bonneReponse: '872',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · lire/écrire jusqu'à 1 000",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      champ_officiel: "nombres jusqu'à 1 000",
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-NB-03',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'MA-CE1-NB-03',
    enonce: 'Combien de dizaines dans 430 ?',
    options: ['3', '4', '43', '430'],
    bonneReponse: '43',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · décomposer",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      champ_officiel: "nombres jusqu'à 1 000",
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-NB-04',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'MA-CE1-NB-04',
    enonce: 'Range : 567, 576, 657.',
    options: ['567 < 576 < 657', '576 < 567 < 657', '657 < 576 < 567', '567 < 657 < 576'],
    bonneReponse: '567 < 576 < 657',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · ranger jusqu'à 1 000",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      champ_officiel: "nombres jusqu'à 1 000",
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-NB-05',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-NB-05',
    enonce: 'Complète : 654 = 600 + 50 + ___ .',
    bonneReponse: '4',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · décomposition",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      champ_officiel: "nombres jusqu'à 1 000",
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CALCUL_MENTAL: CE1Item[] = [
  {
    id: 'MA-CE1-CAL-01',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-CAL-01',
    enonce: 'Combien font 6 × 7 ?',
    bonneReponse: '42',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · tables multiplication',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-CAL-02',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-CAL-02',
    enonce: 'Combien font 25 + 17 ? (calcul mental)',
    bonneReponse: '42',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · addition mentale',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-CAL-03',
    schoolLevel: 'CE1',
    archetype: 'vf',
    competence: 'MA-CE1-CAL-03',
    enonce: '8 × 8 = 64. Vrai ou faux ?',
    bonneReponse: true,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-CAL-04',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-CAL-04',
    enonce: 'Combien font 50 - 23 ? (calcul mental)',
    bonneReponse: '27',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · soustraction mentale',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-CAL-05',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-CAL-05',
    enonce: 'Combien font le double de 35 ?',
    bonneReponse: '70',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · doubles',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_OPERATIONS: CE1Item[] = [
  {
    id: 'MA-CE1-OP-01',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-OP-01',
    enonce: "Pose l'opération 67 + 28. Quel est le résultat ?",
    bonneReponse: '95',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · addition posée avec retenue',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-OP-02',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-OP-02',
    enonce: "Pose l'opération 854 + 269. Quel est le résultat ?",
    bonneReponse: '1123',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · addition jusqu'à 1 000",
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-OP-03',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-OP-03',
    enonce: "Pose l'opération 78 - 35. Quel est le résultat ?",
    bonneReponse: '43',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · soustraction posée',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_PROBLEMES: CE1Item[] = [
  {
    id: 'MA-CE1-PROB-01',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-PROB-01',
    enonce: "Tom a 23 billes. Il en gagne 15. Combien maintenant ?",
    bonneReponse: '38',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · Résolution · 1 étape',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-PROB-02',
    schoolLevel: 'CE1',
    archetype: 'saisie',
    competence: 'MA-CE1-PROB-02',
    enonce: "Léa a 45 bonbons. Elle en donne 18 à son frère. Combien lui en reste-t-il ?",
    bonneReponse: '27',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · Résolution · 1 étape',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-PROB-03',
    schoolLevel: 'CE1',
    archetype: 'qcm',
    competence: 'MA-CE1-PROB-03',
    enonce: "Sur un gâteau, on a planté 24 bougies. 5 sont éteintes. Combien allumées ?",
    options: ['19', '29', '5', '24'],
    bonneReponse: '19',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · Résolution · 1 étape',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE1-PROB-04',
    schoolLevel: 'CE1',
    archetype: 'texte-a-trou',
    competence: 'MA-CE1-PROB-04',
    enonce: "Pierre avait 150 images. Il en donne 45 puis en achète 30. Combien maintenant ?",
    enonceSegments: ['Pierre avait 150 images. Il en donne 45 puis en achète 30. Combien maintenant ? Réponse : ', '{T1}'],
    trous: [{ id: 'T1', bonneReponse: '135', tolerance: 0 }],
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE1 · Mathématiques · Résolution · 2 étapes',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce1: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

export const BANQUE_CE1: CE1Item[] = [
  ...ITEMS_LECTURE_CODE,
  ...ITEMS_FLUENCE,
  ...ITEMS_COMPREHENSION,
  ...ITEMS_VOCABULAIRE,
  ...ITEMS_ECRITURE_ORTHOGRAPHE,
  ...ITEMS_CONSIGNES,
  ...ITEMS_NUMERATION,
  ...ITEMS_CALCUL_MENTAL,
  ...ITEMS_OPERATIONS,
  ...ITEMS_PROBLEMES,
];

export const BANQUE_CE1_COUNT = BANQUE_CE1.length;
export const BANQUE_CE1_REPARTITION = {
  lecture_code: ITEMS_LECTURE_CODE.length,
  fluence: ITEMS_FLUENCE.length,
  comprehension: ITEMS_COMPREHENSION.length,
  vocabulaire: ITEMS_VOCABULAIRE.length,
  ecriture_orthographe: ITEMS_ECRITURE_ORTHOGRAPHE.length,
  consignes: ITEMS_CONSIGNES.length,
  numeration: ITEMS_NUMERATION.length,
  calcul_mental: ITEMS_CALCUL_MENTAL.length,
  operations: ITEMS_OPERATIONS.length,
  problemes: ITEMS_PROBLEMES.length,
};
